/**
 * aufgaben-api.ts — presentation utils + typed fetch wrapper for the Aufgaben
 * queue (T5, the all-process sibling of the Eingang screen).
 *
 * The queue IS the existing GET /tasks worklist (apiClient.tasks.list) — this
 * module NEVER re-declares the wire contract. It only adds German display
 * labels + a DERIVED priority (from due_at/overdue + process — no schema
 * change, coding-standards: the UI renders server facts, priority is a pure
 * display bucket, not a persisted domain value).
 *
 * [Source: apps/api/src/modules/tasks/tasks.controller.ts GET /tasks]
 * [Source: packages/shared/src/task.ts TASK_PROCESS_VALUES / TaskStatus]
 * [Source: apps/web/src/features/invoices/api/invoices-api.ts (labels/presets precedent)]
 */

import { apiClient } from '../../../lib/api-client';
import type { TaskListParams } from '../../../lib/api-client';
import type { StatusTone } from '../../../components/ui/StatusDot';
import type { BadgeVariant } from '../../../components/ui/Badge';
import { daysUntil } from '../../../lib/format';
import type { TaskProcess, TaskStatus } from '@brokly/shared';
import type { TaskPriority, TaskPreset, TaskRow } from '../types';

// Re-export for convenience
export type { TaskListParams };

// ── Process → German category label (design.md: German UI, English identifiers)
// Covers ALL 20 task_process enum values (append-only mirror of
// packages/shared TASK_PROCESS_VALUES). Readable category names, not raw enums.
export const PROCESS_LABEL: Record<TaskProcess, string> = {
  email_intake: 'E-Mail-Eingang',
  org_genesis: 'Firmenanlage',
  zefix_enrich: 'Firmendaten (Zefix)',
  mandate_signing: 'Mandat',
  status_change: 'Statusänderung',
  doc_classification: 'Dokumentenprüfung',
  invoice_check: 'Rechnungskontrolle',
  other: 'Sonstiges',
  invoice_manual_match: 'Rechnung zuordnen',
  policy_endorsement_review: 'Policenänderung',
  invoice_k2_review: 'Rechnungsprüfung',
  invoice_k3_escalation: 'Rechnung eskaliert',
  commission_manual_match: 'Courtage zuordnen',
  renewal_due: 'Renewal',
  portal_message: 'Kommunikation',
  portal_mutation: 'Portal-Mutation',
  document_upload: 'Dokument-Upload',
  carrier_notification_address: 'Benachrichtigungsadresse',
  mandate_ruecklauf: 'Rücklauf zuordnen',
  insurer_notification: 'Versicherer benachrichtigen',
  mandate_registration: 'Meldung an Versicherer',
  mandate_termination: 'Mandatsauflösung',
  customer_retention: 'Aufbewahrung',
};

/** Process → readable German category label (falls back to the raw code). */
export function processLabel(process: TaskProcess): string {
  return PROCESS_LABEL[process] ?? process;
}

// ── Status → German label + StatusDot tone (dot + text, never a filled pill) ──
export const STATUS_LABEL: Record<TaskStatus, string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  awaiting_approval: 'Wartet auf Freigabe',
  done: 'Erledigt',
  cancelled: 'Abgebrochen',
};

export const STATUS_TONE: Record<TaskStatus, StatusTone> = {
  open: 'neutral',
  in_progress: 'info',
  awaiting_approval: 'warning',
  done: 'success',
  cancelled: 'neutral',
};

/** Status → German label (falls back to the raw code). */
export function statusLabel(status: TaskStatus): string {
  return STATUS_LABEL[status] ?? status;
}

// ── Derived priority — pure DISPLAY bucket (no schema change) ──────────────────
/** Processes that are inherently high-priority regardless of a due date. */
const HIGH_PRIORITY_PROCESSES: ReadonlySet<TaskProcess> = new Set<TaskProcess>([
  'invoice_k3_escalation',
]);

/**
 * derivePriority — 'hoch' (overdue / red · or an escalation process) ·
 * 'mittel' (due within 7 days / orange) · 'tief' (everything else).
 * Pure function of due_at + process; NOT persisted. Terminal tasks (done /
 * cancelled) are always 'tief' (no action pending).
 */
export function derivePriority(task: Pick<TaskRow, 'due_at' | 'process' | 'status'>): TaskPriority {
  if (task.status === 'done' || task.status === 'cancelled') return 'tief';
  if (HIGH_PRIORITY_PROCESSES.has(task.process)) return 'hoch';
  const days = daysUntil(task.due_at);
  if (days === null) return 'tief';
  if (days < 0) return 'hoch'; // overdue
  if (days <= 7) return 'mittel';
  return 'tief';
}

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  hoch: 'Hoch',
  mittel: 'Mittel',
  tief: 'Tief',
};

/** Priority → Badge variant (category/priority badge — design.md §2, never status). */
export const PRIORITY_VARIANT: Record<TaskPriority, BadgeVariant> = {
  hoch: 'danger',
  mittel: 'warning',
  tief: 'neutral',
};

// ── Fälligkeit (due) display ──────────────────────────────────────────────────
/**
 * Human due-date text + tone from due_at. Overdue → "X T überfällig" (danger);
 * today → "Heute"; soon → "in X T"; else the short de-CH date. No domain rule
 * is computed here — display only (daysUntil precedent).
 */
export function faellig(due_at: string | null): { text: string; tone: StatusTone } {
  const days = daysUntil(due_at);
  if (days === null) return { text: '-', tone: 'neutral' };
  if (days < 0) return { text: `${Math.abs(days)} T überfällig`, tone: 'danger' };
  if (days === 0) return { text: 'Heute', tone: 'warning' };
  if (days <= 7) return { text: `in ${days} T`, tone: 'warning' };
  return { text: `in ${days} T`, tone: 'neutral' };
}

// ── Quelle (source) — derived from process (TaskSummary has no created_by_kind) ─
const PROCESS_SOURCE: Partial<Record<TaskProcess, string>> = {
  email_intake: 'E-Mail',
  portal_message: 'Kundenportal',
  portal_mutation: 'Kundenportal',
  invoice_check: 'System',
  invoice_manual_match: 'System',
  invoice_k2_review: 'System',
  invoice_k3_escalation: 'System',
  commission_manual_match: 'System',
  renewal_due: 'System',
  zefix_enrich: 'System',
  org_genesis: 'System',
  doc_classification: 'System',
};

/** Origin label for the Quelle column (best-effort from the process family). */
export function sourceLabel(task: Pick<TaskRow, 'process'>): string {
  return PROCESS_SOURCE[task.process] ?? 'Manuell';
}

/**
 * Assignee line of the Quelle column (issue 20): names come from the list row itself
 * (server-resolved assignee_name / assigned_group_name), never from the admin-gated
 * GET /admin/members. A group-assigned, unclaimed task shows the group name.
 */
export function assigneeLabel(
  task: Pick<TaskRow, 'assigned_to' | 'assigned_group_id' | 'assignee_name' | 'assigned_group_name'>,
): string {
  if (task.assigned_to) return task.assignee_name ?? 'Zugewiesen';
  if (task.assigned_group_id) return task.assigned_group_name ?? 'Gruppe';
  return 'Nicht zugewiesen';
}

// ── Saved-view presets (FilterChips) ──────────────────────────────────────────
// Alle · Meine · Überfällig · Kritisch · Diese Woche. The date/priority buckets are
// pure client-side predicates over the loaded page.
/**
 * Issue 27: "Meine" is the ONE preset the client cannot evaluate. Its server
 * meaning is "assigned to me OR assigned to one of my Funktionsgruppen", and the
 * browser never learns the caller group memberships, so in live mode the screen
 * sends `?mine=true` and renders what comes back. The local `match` below stays
 * for MOCK mode only (no server there), which is why it is the narrow
 * assigned_to test rather than a guess at the group half.
 */
export const MEINE_PRESET_KEY = 'meine';

export const TASK_PRESETS: TaskPreset[] = [
  { key: 'alle', label: 'Alle', match: () => true },
  {
    key: MEINE_PRESET_KEY,
    label: 'Meine',
    match: (r, myUserId) => myUserId !== null && r.assigned_to === myUserId,
  },
  {
    key: 'ueberfaellig',
    label: 'Überfällig',
    match: (r) => {
      const days = daysUntil(r.due_at);
      return days !== null && days < 0;
    },
  },
  {
    key: 'kritisch',
    label: 'Kritisch',
    match: (r) => derivePriority(r) === 'hoch',
  },
  {
    key: 'woche',
    label: 'Diese Woche',
    match: (r) => {
      const days = daysUntil(r.due_at);
      return days !== null && days >= 0 && days <= 7;
    },
  },
];

// ── Fetch wrapper ─────────────────────────────────────────────────────────────
/**
 * Fetch the Aufgaben rows (GET /tasks) with the opt-in total for the "x von y"
 * count. Returns the rows + the server total (null when the backend omits it).
 */
export async function fetchTaskRows(
  params: TaskListParams = {},
): Promise<{ rows: TaskRow[]; total: number | null }> {
  const res = await apiClient.tasks.list({ include_total: true, ...params });
  return { rows: res.data, total: res.page.total_count ?? null };
}
